<template>
<language-setting :show-language-setting="true" :selectedLanguage="localeLanguage" :languages="appLanguages"
@language-selected="onLanguageSelected" />
<div class="live-speaker-playground">
    <div class="left-column">
        <speaker-settings 
            v-model:sourceLanguage="sourceLanguage" 
            v-model:targetLanguage="targetLanguage" 
            v-model:inputDevice="inputDevice" 
            v-model:smoothScrollEnabled="smoothScrollEnabled"
            v-model:diarizationEnabled="diarizationEnabled"
            :isListening="isListening" 
            :isConnecting="isConnecting" 
            :connectFailed="connectFailed" 
            :errorMessage="errorMessage" 
            :hasConnection="hasConnection" 
            :speakingLanguages="speakingLanguages" 
            :languages="languages" 
            :qrCodeUrl="qrCodeUrl" 
            :shareUrl="shareUrl" 
            @start-listening="startListening" 
            @stop-listening="stopListening" 
            @retry-connection="retryConnection" />
    </div>
    <div class="right-column">
        <messages-list 
            :messages="messages" 
            ref="messageContainer" 
            class="connected" 
            :is-listening="isListening" 
            :smoothScrollEnabled="smoothScrollEnabled"
            :diarizationEnabled="diarizationEnabled" />
        <recording-indicator v-if="isListening" :hasConnection="hasConnection" />
    </div>
</div>
</template>

<script>
import {
    LANGUAGES, LANGUAGES_TRANSLATE, APP_LANGUAGES
} from '@/constants/language.js';
import SpeakerSettings from '@/components/SpeakerSetting.vue';
import MessagesList from '@/components/MessageListPlayground.vue';
import RecordingIndicator from '@/components/RecordingIndicator.vue';
import LanguageSetting from '@/components/LanguageSetting.vue';
import SHA256 from 'crypto-js/sha256';
import useAudioService from '@/services/audioService';
import useWebSocketService from '@/services/webSocketService';
import QRCode from 'qrcode';

export default {
    name: 'LiveSpeakerPlayground',
    components: {
        SpeakerSettings,
        MessagesList,
        RecordingIndicator,
        LanguageSetting,
    },
    data() {
        return {
            isListening: false,
            isConnecting: false,
            connectFailed: false,
            hasConnection: false,
            errorMessage: '',
            sourceLanguage: '',
            targetLanguage: '',
            localeLanguage: '',
            inputDevice: 0,
            languages: LANGUAGES_TRANSLATE,
            speakingLanguages: LANGUAGES,
            appLanguages: APP_LANGUAGES,
            messages: [],
            smoothScrollEnabled: true,
            diarizationEnabled: false,
            qrCodeUrl: null,
            audioService: null,
            webSocketService: null,
            mediaRecorder: null,
            deepgramConnection: null,
            speakerSocket: null
        };
    },
    computed: {
        roomId() {
            return SHA256(navigator.userAgent).toString().slice(-20);
        },
        shareUrl() {
            return `${window.location.origin}/${this.roomId}`;
        }
    },
    methods: {
        async startListening() {
            if (!this.sourceLanguage || !this.targetLanguage || this.isConnecting) return;
            this.isConnecting = true;
            this.connectFailed = false;
            this.errorMessage = '';

            if (!this.audioService) this.audioService = useAudioService(this.inputDevice);
            if (!this.webSocketService) {
                this.webSocketService = useWebSocketService(
                    this.sourceLanguage,
                    this.targetLanguage,
                    this.roomId,
                    this.handleMessage,
                    this.diarizationEnabled
                );
            }

            const {
                initAudio,
                startMediaRecorder
            } = this.audioService;
            const {
                connectToDeepgram,
                connectToSpeakerSocket
            } = this.webSocketService;

            try {
                const {
                    audioStream,
                    mediaRecorder,
                    sendAudioChunk
                } = await initAudio();
                if (!audioStream || !mediaRecorder) throw new Error("Could not initialize audio");
                this.mediaRecorder = mediaRecorder;
                this.deepgramConnection = await connectToDeepgram(sendAudioChunk);
                if (!this.deepgramConnection) throw new Error("Deepgram connection not established");
                this.speakerSocket = await connectToSpeakerSocket();
                startMediaRecorder(this.mediaRecorder);

                this.isListening = true;
                this.isConnecting = false;
                this.hasConnection = true;
            } catch (error) {
                console.error('Error starting listening:', error);
                this.errorMessage = error.message || 'Failed to start listening. Please try again.';
                this.isConnecting = false;
                this.connectFailed = true;
                this.cleanupConnections();
            }
        },

        stopListening() {
            this.cleanupConnections();
            this.isListening = false;
            this.hasConnection = false;
        },

        cleanupConnections() {
            if (this.audioService) this.audioService.cleanup();
            if (this.webSocketService) this.webSocketService.cleanup();
            this.mediaRecorder = null;
            this.deepgramConnection = null;
            this.speakerSocket = null;
        },

        retryConnection() {
            this.stopListening();
            setTimeout(() => this.startListening(), 1000);
        },

        handleMessage(message) {
            this.messages.push(message);
            this.scrollToLatestMessage();
        },

        scrollToLatestMessage() {
            this.$nextTick(() => {
                const container = this.$refs.messageContainer;
                if (container) {
                    container.$el.scrollTo({
                        top: container.$el.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            });
        },

        async generateQRCode() {
            try {
                this.qrCodeUrl = await QRCode.toDataURL(this.shareUrl, {
                    width: 1000,
                    margin: 1
                });
            } catch (error) {
                console.error('Failed to generate QR code:', error);
            }
        },
        onLanguageSelected(langCode) {
            this.localeLanguage = langCode
            this.$i18n.locale = langCode
            localStorage.setItem('app-locale', langCode)
            const rtlLanguages = ['ar', 'he', 'fa', 'ur']
            const direction = rtlLanguages.includes(langCode) ? 'rtl' : 'ltr'
            document.documentElement.setAttribute('dir', direction)
        }
    },
    mounted() {
        this.generateQRCode();
    },
    beforeUnmount() {
        this.stopListening();
    }
};
</script>

<style scoped>
.live-speaker-playground {
    padding: 45px;
    display: flex;
    flex-direction: row;
    min-height: 100vh;
    width: 100vw;
    overflow-x: hidden;
    overflow-y: auto;
    box-sizing: border-box;
    justify-content: center;
    align-items: center;
    gap: 16px
}

.left-column {
    flex: 0 0 30%;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    padding-left: 24px;
    max-width: 30vw;
}

.right-column {
    flex: 0 0 70%;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    padding-right: 24px;
    max-width: 70vw;
}

@media screen and (max-width: 768px) {
    .live-speaker-playground {
        flex-direction: column;
        overflow-y: auto;
    }

    .left-column,
    .right-column {
        margin-top: 24px;
        flex: 1 1 100%;
        width: 100vw;
        max-width: 100vw;
        padding: 16px;
    }
}
</style>
